import { type App, inject, reactive } from 'vue'
import type { Router } from 'vue-router'
import { getPriceInclTax, type IOrderAddress, type IOrderItem, type SiteConfig, TaxClass } from '@rogue/universal'
import { FeatureDefinition, type StickyBucketServiceSync } from '@growthbook/growthbook'
import type { Pinia } from 'pinia'
import type { RogueOrder } from '@orders'
import type { IRogueAnalytics, IHeartbeatResp, IRequestConfig, IRogueLog, RogueBrowser, SiteClientKeys, SSRContext } from '@types'
import { checkCompatibility, CompatibilityResult } from '../helpers/compatibility'
import { getOrderErrorMessage } from '@/js/helpers/payment-methods'
import type { IAppContext } from './appContext'
import { BaseI18N } from '@/js/services/i18n/base'
import { FeatureFlags } from '@/js/services/featureFlags'
import { generateCaptchaToken } from '@/js/services/recaptcha/recaptcha'
import { getOosItems, listProductIds, getBuyRequestMeta } from '@/js/helpers/buy-request'
import { getNotifyLayout } from '@/js/customProducts/notifyLayout'
import { gte } from '@/js/helpers/version-compare'
import { useCmsStore } from '@/js/stores/cmsStore'
import { useConfigStore } from '@/js/stores/configStore'
import { useCustomerStore } from '@/js/stores/customerStore'
import { useEventStore } from '@/js/stores/eventStore'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { reloadCartFromNet } from '@/js/pages/checkout/checkCartInventory'
import type { BuyRequest, CMS, IRogue, IRogueApi, IRogueI18N, IRogueRfk, IRogueCnstrc, Product } from '@/js/services/RogueTypes'

export interface RogueOptions {
  features: Record<string, FeatureDefinition>
  site: SiteConfig,
  initialRoute: string,
  baseUrl: string,

  forceFeatures: { [key: string]: any },
  // Growthbook dateUpdated of `features`, from the SSR state
  featuresUpdatedDate?: string,
  env: 'test' | 'sprint' | 'staging' | 'prod',
  appContext: IAppContext | null,
  l10n: RogueBrowser['l10n'],
  debug?: boolean,
  keys: SiteClientKeys,
  rfk: IRogueRfk,
  cnstrc: IRogueCnstrc,
  api: IRogueApi
  log: IRogueLog,
  analytics: IRogueAnalytics,
  ssr: boolean,
  conversionRates: { [key: string]: number }
  // Growthbook sticky bucket storage. Browser only — supplied by entry-client,
  // which owns cookie access; SSR passes none (see FeatureFlags.getHashId).
  stickyBuckets?: StickyBucketServiceSync
}

export type CurrencyOptions = {
  plusSymbol?: boolean,
  euTaxClass?: true | TaxClass,
  useLocale?: boolean,
  // Allow GBP for UK store
  allowLocalCurrency?: boolean
  currency?: string,
  hideFractionalUnits?: boolean
}

export class Rogue implements IRogue {
  site: SiteConfig

  rfk: IRogueRfk

  cnstrc: IRogueCnstrc

  initialRoute: string

  appContext: IAppContext | null = null

  log: IRogueLog

  api: IRogueApi

  i18n: IRogueI18N

  debug: boolean = false

  track: IRogueAnalytics

  network: IRogue['network']

  l10n: RogueBrowser['l10n']

  app?: App

  hasNewRelease?: string | undefined = undefined

  ssr: boolean = false

  pinia?: Pinia

  baseURL: string

  localizedBaseURL: string

  theIndexBaseURL: string

  keys: SiteClientKeys

  serviceWorker: ServiceWorker | null

  env: 'test' | 'sprint' | 'staging' | 'prod'

  router?: Router

  session = {
    start      : Date.now(),
    lastActive : Date.now(),
  }

  // Loaded Modules (or adyen order). Observable so it can be used in templates
  loaded = reactive({
    checkout: false,
  })

  moduleLoadPromises: { [key: string]: Promise<void> } = {}

  ssrContext?: SSRContext

  routeResp?: {
    related?: {
      AUSTRALIA?: string;
      EUROPE?: string;
      US?: string;
      CANADA?: string;
      UK?: string;
    }
  }

  featureFlags: FeatureFlags

  observeData: {
    unrecoverableError?: boolean
    watcherTest: null | number
  }

  conversionRates: { [key: string]: number }

  constructor(options: RogueOptions) {
    // Properties
    this.site = options.site

    this.initialRoute = options.initialRoute

    this.appContext = options.appContext
    this.ssr = options.ssr || false
    this.serviceWorker = null

    // For EU, we want to track what country we are shipping to so we can update VAT

    // If Vue errors, we may need to check if watchers are still responding
    this.observeData = reactive({ watcherTest: null })

    this.debug = options.debug || false

    this.env = options.env || 'prod'

    this.api = options.api

    this.api.init(this)

    const L10N_DEFAULTS: RogueBrowser['l10n'] = {
      lang     : 'en',
      locale   : this.site.locales?.[0] || 'en-US',
      path     : '',
      currency : this.site.currency,
      country  : this.site.defaultCountry,
      index    : true,
    }

    // Make sure we have the defaults set
    this.l10n = reactive(Object.assign({}, L10N_DEFAULTS, options.l10n))
    // Set up the locale correctly
    this.l10n.locale = `${this.l10n.lang}-${this.l10n.country}`
    this.i18n = BaseI18N

    this.log = options.log

    this.track = options.analytics
    this.rfk = options.rfk
    this.cnstrc = options.cnstrc
    this.network = reactive({ isOffline: false })
    this.baseURL = options.baseUrl

    this.localizedBaseURL = options.baseUrl + this.l10n.path

    this.keys = options.keys

    this.rfk.init(this)
    this.cnstrc.init(this)

    this.track.init(this)

    this.theIndexBaseURL = ['staging', 'prod'].includes(this.env) && !this.baseURL.includes('local') ? this.baseURL : `https://${this.site.hostname}`
    this.featureFlags = new FeatureFlags(this, options.features, options.forceFeatures, options.stickyBuckets, options.featuresUpdatedDate)

    this.conversionRates = options.conversionRates

    this.updateShipTo(this.l10n.country)
  }

  __(string: string | number | null | undefined, args?: any, ns?: string) {
    return this.i18n.translate(string, args, ns)
  }

  getErrorMessage(error: Parameters<typeof getOrderErrorMessage>[0]): string {
    const { key, args } = getOrderErrorMessage(error)
    
    return this.__(key, args).replace(/<[^>]*>/g, '')
  }

  init(app: App) {
    this.app = app
  }

  setNetworkStatus(online?: boolean) {
    if (online === undefined && !this.ssr) {
      online = window.navigator.onLine
    }
    if (online !== undefined && this.network.isOffline !== !online) {
      this.network.isOffline = !online
      this.log.crumb('network', `BROWSER WENT ${online ? 'online' : 'offline'}`)
      this.log.set('tag', { offline: !online })
    }
  }

  // checkRelease false skips the release check, which can start the service worker precaching a new build
  checkHeartbeat(checkRelease = true) {
    if (this.ssr) {
      return
    }

    this.fetch<IHeartbeatResp>('/svc_ssr/heartbeat').then(resp => {
      const pageHash = window.ROGUE.release?.hash
      if (checkRelease && resp && resp.hash && pageHash && resp.hash !== pageHash) {
        this.loadRelease(resp)
      }

      if (resp.features) {
        this.featureFlags.updateFeatures(resp.features, resp.featuresUpdatedDate)
      }
    }).catch(() => {
      // Ignore hash errors
    })
  }

  loadRelease(release: RogueBrowser['release'], forcePreload = false) {
    const current = window.ROGUE.release
    if (!release || !current) {
      return
    }

    if (new Date(current.buildDate || 0) > new Date(release.buildDate)) {
      this.log.info('Heartbeat had older release than current release', { current, release })

      return
    }

    const isNew = release.hash !== current.hash && release.hash !== this.hasNewRelease

    if (isNew || forcePreload) {
      this.serviceWorker?.postMessage({ type: 'PRELOAD_RELEASE', releaseHash: release.hash })
    }

    if (isNew) {
      this.log.crumb('release', `Downloading new release (current: ${current.buildDate} New: ${release.buildDate})`)
      this.log.info(`New release found (current: ${current.buildDate} New: ${release.buildDate})`)
      this.hasNewRelease = release.hash
    }
  }

  getProductById(id: number): Product {
    const productStore = useProductStore(this.pinia)

    return productStore.products[id]
  }

  async updateListItem(list: string, buyRequest: BuyRequest): Promise<void> {
    const listStore = useListStore(this.pinia)

    await listStore.updateListItem(this, list, buyRequest._id, buyRequest)
  }

  setPinia(pinia: Pinia) {
    this.pinia = pinia
  }

  emitToApp(message: string, data: any = {}) {
    const payload = {
      data,
      category  : message,
      app       : true,
      mobileApp : true,
      url       : window.location.pathname,
    }
    if (this.isApp('3.3.0')) {
      if (window.ReactNativeWebView) {
        window.ReactNativeWebView.postMessage(JSON.stringify(payload))
      } else {
        console.warn('Rogue::emitToApp', payload)
      }
    } else {
      window.parent.postMessage(payload, '*')
    }
  }

  isApp(version = '') {
    if (!this.appContext) {
      return false
    }

    if (version.length === 0) {
      return true
    }

    if (this.appContext.version) {
      return gte(this.appContext.version, version)
    }

    return false
  }

  setRouter(router: Router) {
    this.router = router
  }

  async fetchWithCaptcha<T>(url: string, method: IRequestConfig['method'] = 'GET', body = {}, headers: Record<string, string>, responseType: 'json' | 'blob' = 'json', action: string): Promise<T> {
    headers['RG-RCPTH-TOKEN'] = await generateCaptchaToken(action, this)

    return this.fetch<T>(url, method, body, headers, responseType)
  }

  fetch<T>(url: string, method: IRequestConfig['method'] = 'GET', body = {}, headers: Record<string, string> = {}, responseType: 'json' | 'blob' = 'json'): Promise<T> {
    const requestConfig: IRequestConfig = { url, method, headers, responseType }

    if (this.appContext && !headers['RG-RCPTH-TOKEN']) {
      requestConfig.url = `${requestConfig.url}${requestConfig.url.includes('?') ? '&' : '?'}isMobileApp=1&mobileAppVersion=${this.appContext.version}&mobileAppPlatform=${this.appContext.platform}`
    }

    if (method !== 'GET') {
      requestConfig.data = body
    }

    return this.api.fetch<T>(requestConfig)
  }

  async share(shareObj: { url: string, title: string, text?: string }, config?: { preventNative?: boolean, el?: Node | null }): Promise<'clipboard' | 'native' | 'select' | 'none'> {
    if (this.appContext && !config?.preventNative) {
      this.emitToApp('share_link', shareObj)

      return 'native'
    }

    if (navigator.share && !config?.preventNative) {
      await navigator.share(shareObj)

      return 'native'
    }

    if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareObj.url)

      return 'clipboard'
    }

    if (config?.el && typeof window !== 'undefined') {
      try {
        window.getSelection()?.selectAllChildren(config.el)

        return 'select'
      } catch (e) {
        //
      }
    }

    return 'none'
  }

  updateShipTo(countryId: string) {
    if (!countryId) {
      return
    }

    let countryChanged = false
    if (this.l10n.country !== countryId) {
      this.l10n.country = countryId
      countryChanged = true
    }

    // @ts-ignore
    this.l10n.currency = this.site.currency

    // Update available adyen payment types to match the selected country
    if (countryChanged && this.adyenCheckout && this.loaded.checkout && this.pinia) {
      import('@/js/stores/paymentMethodStore').then(pms => {
        const paymentMethodStore = pms.usePaymentMethodStore(this.pinia)
        paymentMethodStore.loadPaymentMethods(this, true).then(() => {
          // If the customer selected a payment method and then updated the shipping address so that
          // the payment type is no longer available, we need to reset the payment method.
          import('../pages/checkout/checkout').then(co => {
            co.initOrder(this).then((order) => {
              if (order.data.payment.paymentType === 'adyen') {
                // Reseting the adyenInstance forces the AdyenCheckout object to use the updated payment types and card brands
                order.adyenInstance = undefined

                if (!paymentMethodStore.availableOptions.find(pm => pm.key === order.data.payment.adyenType)) {
                  co.getDefaultOrderProps(this).then(dflt => {
                    order.updatePayment(dflt?.payment ?? { amount: 0, paymentType: 'adyen', adyenType: 'scheme' })
                  })
                }
              }
            })
          })
        })
      })
    }
  }

  currency(number: string | number, opts: CurrencyOptions = {}) {
    let locale = 'en-US'
    if (opts.useLocale || (this.site.code === 'EUR' || this.site.code === 'GBR')) {
      locale = this.l10n.locale
    }
    let additionalPlusSymbol = opts.plusSymbol
    let taxClass: TaxClass | undefined
    let val = typeof number === 'string' ? parseFloat(number) : number
    const nan = number === null || number === undefined || Number.isNaN(val)

    if (nan) {
      val = 0
    }

    const currency = opts.currency || this.site.currency

    if (['EUR', 'GBR'].includes(this.site.code)) {
      if (opts.euTaxClass === true) {
        taxClass = TaxClass.EUGoods
      } else {
        taxClass = opts.euTaxClass
      }

      if (taxClass) {
        val = getPriceInclTax(val, taxClass, this.l10n.country || undefined)
      }
    }

    additionalPlusSymbol = additionalPlusSymbol && val > 0
    const formatOptions: Intl.NumberFormatOptions = {
      style: 'currency',
      currency,
    }
    if (opts.hideFractionalUnits) {
      formatOptions.maximumFractionDigits = 0
      formatOptions.minimumFractionDigits = 0
    }

    const retval = (val || 0).toLocaleString(locale, formatOptions)

    if (nan) {
      return retval.replace(/0+/g, '—')
    }

    return additionalPlusSymbol ? `+${retval}` : retval
  }

  // Functions for the Order Servie
  observable<T extends object>(obj: T) {
    return reactive<T>(obj) as T
  }

  async saveAddress(addr: IOrderAddress) {
    const customerStore = useCustomerStore(this.pinia)

    return (await customerStore.saveAddress(addr, this)) as IOrderAddress & { _id: string }
  }

  loadProducts(ids: number[]): Promise<void> {
    const productStore = useProductStore(this.pinia)

    return productStore.loadAll(ids, this)
  }

  async reloadOrderItems(items: IOrderItem[]) {
    const productStore = useProductStore(this.pinia)

    // Reload all products (full:true means to also replace attributes)
    await Promise.all(listProductIds(items, false)
      .map(productId => productStore.reload(productId, true, this)))

    const listStore = useListStore(this.pinia)
    await listStore.sendCartItems(this)
  }

  async reloadCartFromNet(order: RogueOrder) {
    await reloadCartFromNet(this, order)
  }

  oosItems(items: IOrderItem[]) {
    const productStore = useProductStore(this.pinia)

    return getOosItems(items, productStore.products || [], this)
  }

  getCms(): { [key: string]: CMS } {
    if (!this.pinia) return {}

    const cmsStore = useCmsStore(this.pinia)

    return cmsStore.cms
  }

  setCmsIdForUrl(url: string, id: number) {
    if (this.pinia) {
      const cmsStore = useCmsStore(this.pinia)
      cmsStore.setIdForUrl(url, id)
    }
  }

  loadCms(cmsId: string | number): Promise<void | CMS | undefined> {
    if (!this.pinia) return Promise.resolve()

    const cmsStore = useCmsStore(this.pinia)

    return cmsStore.load(cmsId, this)
  }

  async submitWufooForm(hash: string, data: { [key: string]: string }) {
    const formData = new FormData()

    Object.keys(data).forEach((key) => formData.append(key, data[key]))
    formData.append('formHash', hash)

    return this.fetch('/svc_ssr/submit/wufoo', 'POST', formData)
  }

  /* Added to allow form submission to remove an email from the mailing list.
     This does not remove their records for newsletters in the customer store.
     May eventually need to discuss a backsync for these */
  async submitIterableNewsletterSubUpdates(data: { data: { email: string, emailListIds?: [{ id: number; action: string }] } }) {
    const postData = {
      email        : data.data.email,
      emailListIds : data.data?.emailListIds || [],
    }

    return this.fetch('/svc_ssr/submit/iterable', 'POST', postData)
  }

  // Methods for the RogueOrderService implementation
  get customerGroupId() {
    const customerStore = useCustomerStore(this.pinia)

    return customerStore.groupId || 0
  }

  get addresses() {
    const customerStore = useCustomerStore(this.pinia)

    return customerStore.addresses as IOrderAddress[]
  }

  get countryData(): { alpha2: string; requireRegion: boolean; postcodeRegex?: string }[] {
    if (!this.pinia) return []

    const configStore = useConfigStore(this.pinia)

    return configStore.countries.map(country => ({
      alpha2        : country.alpha2,
      requireRegion : country.requireRegion ?? false,
      postcodeRegex : country.postcodeRegex,
    }))
  }

  get adyenCheckout(): boolean {
    if (!this.keys.adyen) {
      return false
    }

    return Boolean(this.featureFlags.isOn('adyen-checkout') || (this.initialRoute || '').indexOf('redirectResult=') !== -1)
  }

  get earnedProducts() {
    const customerStore = useCustomerStore(this.pinia)

    return customerStore.activeEarnedProducts || {}
  }

  get btwbProfile() {
    const eventStore = useEventStore(this.pinia)

    return eventStore.profile
  }

  getBuyRequestThumbnail(buyRequest: BuyRequest): string {
    return getBuyRequestMeta(buyRequest, this).thumbnail
  }

  checkCompatibility(item: BuyRequest): CompatibilityResult | null {
    return checkCompatibility(this, item)
  }

  getNotifyLayout(buyRequest: BuyRequest, parent?: BuyRequest): Record<string, string> | undefined {
    return getNotifyLayout(this, buyRequest, parent)
  }
}

export function useRogue(): IRogue {
  const rogue = inject<IRogue>('ROGUE')
  if (!rogue) {
    throw new Error('Global context not provided')
  }

  return rogue
}
